import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import { useHistory, useParams } from "react-router-dom";
import styles from "./PurchaseOrderPrintPreview.module.css";

const lineList = (lines = []) => (
  <Box>
    {lines.map((line, index) => (
      <Typography key={`${line}-${index}`} variant="body2">
        {line}
      </Typography>
    ))}
  </Box>
);

const PackingSlipTemplate = ({ payload }) => {
  const { instance, project, purchaseOrder, supplier, shipTo, billTo, items, notes } = payload;

  return (
    <Box className={styles.printCard}>
      <Box className={styles.headerRow}>
        <Box className={styles.headerInfo}>
          {instance?.logoUrl && (
            <img
              className={styles.logo}
              src={instance.logoUrl}
              alt={`${instance.instanceName || "Instance"} logo`}
            />
          )}
          <Box>
            <Typography variant="h5">Packing Slip</Typography>
            <Typography variant="subtitle2">
              {instance?.instanceName || "Elite Core"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider />

      <Box className={styles.twoCol}>
        <Box className={styles.addressBlock}>
          <Typography className={styles.label}>PO Details</Typography>
          <Typography variant="body2">
            <strong>PO #:</strong> {purchaseOrder?.poNumber || "—"}
          </Typography>
          <Typography variant="body2">
            <strong>Project #:</strong> {project?.orderId || "—"}
          </Typography>
          <Typography variant="body2">
            <strong>Project Name:</strong> {project?.projectName || "—"}
          </Typography>
          <Typography variant="body2">
            <strong>Supplier:</strong> {supplier?.name || "—"}
          </Typography>
        </Box>
        <Box className={styles.addressBlock}>
          <Typography className={styles.label}>Ship To</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {shipTo?.name || "—"}
          </Typography>
          {lineList(shipTo?.lines)}
        </Box>
      </Box>

      <Box className={styles.twoCol}>
        <Box className={styles.addressBlock}>
          <Typography className={styles.label}>Bill To</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {billTo?.name || "—"}
          </Typography>
          {lineList(billTo?.lines)}
        </Box>
        <Box className={styles.addressBlock}>
          <Typography className={styles.label}>Notes</Typography>
          <Typography variant="body2" className={styles.noteBlock}>
            {notes || "—"}
          </Typography>
        </Box>
      </Box>

      <Divider />

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Qty</TableCell>
            <TableCell>Details</TableCell>
            <TableCell>Decoration Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(items || []).map((item) => (
            <TableRow key={item.id || item.productId}>
              <TableCell>{item.name || "—"}</TableCell>
              <TableCell>{item.description || "—"}</TableCell>
              <TableCell>{item.quantity || "—"}</TableCell>
              <TableCell>
                {[item.color, item.size].filter(Boolean).join(" • ") || "—"}
              </TableCell>
              <TableCell>
                {(item.decorationNotes || []).length
                  ? item.decorationNotes.join("; ")
                  : "—"}
              </TableCell>
            </TableRow>
          ))}
          {(items || []).length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                No items available.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

const ShippingLabelTemplate = ({ payload }) => {
  const { instance, purchaseOrder, project, shipTo, fromAddress } = payload;
  const fromLines = fromAddress?.lines || [];
  const trackingCount = Array.isArray(purchaseOrder?.trackingNumbers)
    ? purchaseOrder.trackingNumbers.filter(Boolean).length
    : String(purchaseOrder?.trackingNumbers || "")
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean).length;

  const scanValue = [
    `instance=${instance?.instanceUniqueId || ""}`,
    `order=${project?.orderId || ""}`,
    `po=${purchaseOrder?.poNumber || purchaseOrder?.id || ""}`,
  ].join("|");

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(scanValue)}`;

  return (
    <Box className={styles.printCard}>
      <Box className={styles.headerRow}>
        <Box className={styles.headerInfo}>
          {instance?.logoUrl && (
            <img
              className={styles.logo}
              src={instance.logoUrl}
              alt={`${instance.instanceName || "Instance"} logo`}
            />
          )}
          <Box>
            <Typography variant="h5">Shipping Label</Typography>
            <Typography variant="subtitle2">
              {instance?.instanceName || "Elite Core"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider />

      <Box className={styles.twoCol}>
        <Box className={styles.addressBlock}>
          <Typography className={styles.label}>Ship To</Typography>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {shipTo?.name || "—"}
          </Typography>
          {lineList(shipTo?.lines)}
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Reference:</strong> PO {purchaseOrder?.poNumber || "—"}
          </Typography>
        </Box>
        <Box className={styles.addressBlock}>
          <Typography className={styles.label}>From</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {fromAddress?.companyName || instance?.instanceName || "—"}
          </Typography>
          {lineList(fromLines)}
        </Box>
      </Box>

      <Box className={styles.qrSection}>
        <Box>
          <Typography className={styles.label}>Scan / Box Check-in</Typography>
          <Typography variant="body2">
            Scan this QR on a phone to quickly reference this shipment in Elite Core.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>PO:</strong> {purchaseOrder?.poNumber || "—"}
          </Typography>
          <Typography variant="body2">
            <strong>Project:</strong> {project?.orderId || "—"}
          </Typography>
          <Typography variant="body2">
            <strong>Boxes:</strong> {trackingCount || 1}
          </Typography>
        </Box>
        <Box className={styles.qrBox}>
          <img src={qrSrc} alt="Shipping label QR code" className={styles.qrImage} />
        </Box>
      </Box>
    </Box>
  );
};

export default function PurchaseOrderPrintPreview() {
  const { poId, docType, instanceUniqueId } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");
  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;

  const resolvedDocType = useMemo(() => {
    if (docType === "shipping-label") return "shipping-label";
    return "packing-slip";
  }, [docType]);

  useEffect(() => {
    const fetchPayload = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(
          `${SERVER}/purchase-order/${poId}/print/${resolvedDocType}`,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );
        setPayload(res.data);
        document.title = `${resolvedDocType === "packing-slip" ? "Packing Slip" : "Shipping Label"} - ${
          res.data?.purchaseOrder?.poNumber || poId
        }`;
      } catch (err) {
        setError("Unable to load print preview. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchPayload();
  }, [SERVER, poId, resolvedDocType]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    history.push(`/instance/${instanceUniqueId}/sales/orders/${payload?.project?.orderId || ""}/purchase-order-home`);
  };

  if (loading) {
    return (
      <Box className={styles.printRoot} sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={styles.printRoot}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.printRoot}>
      <Box className={styles.printActions}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
      </Box>
      <Box sx={{ mt: 2 }}>
        {resolvedDocType === "shipping-label" ? (
          <ShippingLabelTemplate payload={payload} />
        ) : (
          <PackingSlipTemplate payload={payload} />
        )}
      </Box>
    </Box>
  );
}
